import { notification } from 'antd';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UserProfilePopover from '../../../../../src-new/components/popovers/UserProfilePopover';

import { getDateTextLongFormat } from '../../../../../js/functions';
import { getWorkspacePalette } from '../../../../../js/helper';
import { getUserById } from '../../../../../redux/assignees/selectors';
import { getOpenedTaskId } from '../../../../../redux/general/selectors';
import {
  getOpenedTask,
  getOpenedTaskComments,
  getOpenedTaskCreatedAt,
  getOpenedTaskCreatedBy,
  getOpenedTaskRoom,
  isOpenedTaskLoading,
} from '../../../../../redux/tasks/taskEntities/selectors';
import {
  registerComponentLevelSocketListener,
  unregisterComponentLevelSocketListener,
} from '../../../../../redux/state-areas/socket/actions';

import ActivityViewer from '../../../atoms/ticket-drawer/ActivityViewer';
import DrawerCommentSection from '../../global/DrawerCommentSection';
import TaskDetailsCommentsInput from './TaskDetailsCommentsInput';
import { Skeleton } from '@mui/material';
import { isEmpty } from 'lodash';
import { useGetWorkspaceFormsQuery } from '../../../../../services/formsApi';

const CreatedBySkeleton = () => (
  <div className="flex" style={{ padding: '0.5rem' }}>
    <div style={{ width: '40px' }}>
      <Skeleton
        variant="circular"
        width="28px"
        height="28px"
        className="mr-r"
      />
    </div>
    <Skeleton variant="text" width="250px" style={{ fontSize: '14px' }} />
  </div>
);

const LatestActivitySkeleton = () => (
  <div className="ml-4 flex-col">
    <Skeleton variant="text" width="250px" sx={{ fontSize: '14px' }} />
    <Skeleton
      variant="text"
      width="100px"
      sx={{ fontSize: '14px' }}
      className="mt-1"
    />
  </div>
);

const CommentSkeleton = () => (
  <div>
    <div className="date-row mx-10 mt-2">
      <div className="px-4">
        <Skeleton
          variant="rounded"
          width="80px"
          style={{
            borderRadius: '10px',
            padding: '2px 7px',
            fontSize: '13px',
            height: '28px',
          }}
        />
      </div>
      <Skeleton
        variant="rectangular"
        height="2px"
        sx={{
          width: 'calc(50% - 40px)',
          position: 'absolute',
          bottom: 'calc(50% - 1px)',
          left: 0,
        }}
      />
      <Skeleton
        variant="rectangular"
        height="2px"
        sx={{
          width: 'calc(50% - 40px)',
          position: 'absolute',
          bottom: 'calc(50% - 1px)',
          right: 0,
        }}
      />
    </div>
    <div className="flex" style={{ padding: '0.5rem' }}>
      <div style={{ width: '40px' }}>
        <Skeleton
          variant="circular"
          width="28px"
          height="28px"
          className="mr-r"
        />
      </div>
      <div className="flex-col">
        <Skeleton variant="text" width="200px" style={{ fontSize: '18px' }} />
        <Skeleton variant="text" width="400px" style={{ fontSize: '14px' }} />
      </div>
    </div>
  </div>
);

TimeAgo.addLocale(en);

const TaskDetailsCommentSection = ({
  allUsers,
  deactivated,
  createdAt,
  createdBy,
  isModal,
  isTaskLoading,
  taskComments,
  taskCreatorAvi,
  taskId,
  taskRoom,
  task,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const commentsScrollRef = useRef();

  const workspaceId = useSelector((s) => s.workspaceReducer?.currentWorkspace?._id);
  // Use workspace-scoped query so forms from any project (or intake forms with no project) are found
  const { data: forms } = useGetWorkspaceFormsQuery(
    { workspaceId },
    { skip: !workspaceId || task?.source !== 'form' },
  );

  const [comments, setComments] = useState(
    taskComments?.filter((c) => c?.type !== 'activity') || [],
  );

  const timeAgo = useMemo(() => new TimeAgo(intl.locale), [intl.locale]);

  const taskCreationTime = useMemo(() => {
    const date = new Date(createdAt);
    return intl.formatDate(date, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdAt]);

  const createdByLabel = useMemo(() => {
    switch (createdBy) {
      case 'Form': {
        const formName = forms?.data?.find(
          (f) => f?._id === task?.sourceId,
        )?.formTitle;

        return formName ? `Form submission from "${formName}"` : 'Form submission';
      }
      default: {
        if (task?.source === 'form') {
          const formName = forms?.data?.find(
            (f) => f?._id === task?.sourceId,
          )?.formTitle;
          return formName ? `Form submission from "${formName}"` : 'Form submission';
        }
        const label = allUsers?.find(
          (member) => member?._id === createdBy,
        )?.fullName;
        if (!label) {
          const user = deactivated?.find(
            (member) => member?._id === createdBy,
          )?.fullName;
          return user ? user + ' (deactivated)' : '---';
        }
        return label;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, createdBy, deactivated]);

  const latestActivity = useMemo(
    () =>
      [...(taskComments || [])]
        ?.reverse()
        ?.find((c) => c?.type === 'activity') || '',
    [taskComments],
  );

  const latestActivityTime = useMemo(
    () =>
      latestActivity?.createdAt
        ? timeAgo?.format(new Date(latestActivity?.createdAt))
        : '',
    [latestActivity?.createdAt, timeAgo],
  );

  let messageDate = '';

  useEffect(() => {
    if (!commentsScrollRef || !commentsScrollRef.current) return;

    const listenerId = dispatch(
      registerComponentLevelSocketListener('message-received', (msg) => {
        if (msg?.taskId?.toString() === taskId?.toString()) {
          const scrollHeight = commentsScrollRef?.current?.scrollHeight;
          const height = commentsScrollRef?.current?.clientHeight;
          const maxScrollTop = scrollHeight - height;
          setTimeout(() => {
            if (commentsScrollRef && commentsScrollRef.current) {
              commentsScrollRef.current.scrollTop =
                maxScrollTop > 0 ? maxScrollTop + 100 : 0;
            }
          }, 100);
        }
      }),
    );

    return () => {
      dispatch(
        unregisterComponentLevelSocketListener('message-received', listenerId),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => {
    setComments(taskComments?.filter((c) => c?.type !== 'activity') || []);
  }, [taskComments]);

  return (
    <div className="task-details-comments-section-content-wrapper">
      <div
        className="task-details-comments-list-wrapper mt-2"
        ref={commentsScrollRef}
      >
        {isTaskLoading && !createdByLabel && <CreatedBySkeleton />}
        {(() => {
          if (task?.source === 'form') {
            const sourceForm = forms?.data?.find((f) => f?._id === task?.sourceId);
            return (
              <button
                onClick={() => {
                  if (!sourceForm) {
                    notification.error({ message: 'Form not found. It may have been deleted.', placement: 'bottomRight' });
                    return;
                  }
                  const formProjectId = sourceForm.projects?.[0];
                  if (formProjectId) {
                    navigate(`/${formProjectId}/form?formId=${sourceForm._id}&formView=submissions`);
                  } else {
                    navigate(`/forms?formId=${sourceForm._id}&formView=submissions`);
                  }
                }}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 my-1 rounded-lg border border-indigo-100 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-200 transition-all group"
              >
                <div className="w-7 h-7 rounded-full bg-indigo-100 border border-indigo-200 flex items-center justify-center shrink-0 group-hover:bg-indigo-200 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-indigo-700 truncate">
                    {sourceForm ? `Submitted via "${sourceForm.formTitle}"` : 'Form submission'}
                  </div>
                  <div className="text-[10px] text-indigo-400 font-medium mt-0.5">{taskCreationTime}</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            );
          }

          const creator = allUsers?.find((m) => m?._id === createdBy);
          return (
            <UserProfilePopover member={creator} i={0}>
              <button className="w-full text-left flex items-center gap-3 px-3 py-2.5 my-1 rounded-lg border border-transparent hover:border-slate-200 hover:bg-slate-50 transition-all group">
                {(() => {
                  const hasRealAvi = taskCreatorAvi && !taskCreatorAvi.includes('no_avi.png');
                  if (hasRealAvi) {
                    return (
                      <div className="shrink-0 rounded-full overflow-hidden" style={{ width: 36, height: 36 }}>
                        <img
                          src={taskCreatorAvi}
                          alt={createdByLabel}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </div>
                    );
                  }
                  const palette = getWorkspacePalette(createdByLabel || '');
                  const initials = createdByLabel
                    ? createdByLabel.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
                    : '?';
                  return (
                    <div
                      className="shrink-0 rounded-full flex items-center justify-center text-[12px] font-bold"
                      style={{ width: 36, height: 36, background: palette?.bg, color: palette?.text }}
                    >
                      {initials}
                    </div>
                  );
                })()}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-600 truncate">
                    <span className="font-semibold text-slate-800">{createdByLabel}</span>
                    {' created this task.'}
                  </div>
                  <div className="text-[10px] text-slate-400 font-medium mt-0.5">{taskCreationTime}</div>
                </div>
              </button>
            </UserProfilePopover>
          );
        })()}
        {latestActivity && (
          <div className="ml-4">
            <ActivityViewer
              chat={latestActivity}
              time={latestActivityTime}
              isLatestActivity
            />
          </div>
        )}
        {isTaskLoading && !latestActivity && <LatestActivitySkeleton />}
        {isTaskLoading && isEmpty(comments) && <CommentSkeleton />}
        {comments?.map((chat) => {
          const date = new Date(chat?.createdAt);
          const time = timeAgo.format(date);

          const sender = chat?.sender;

          let showDateRow = false;
          const currentMessageDate = new Date(
            chat?.createdAt,
          ).toLocaleDateString('en-us', {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });

          if (currentMessageDate !== messageDate) {
            showDateRow = true;
            messageDate = currentMessageDate;
          }

          return (
            <div key={chat?._id}>
              {showDateRow && (
                <div className="date-row mx-10 mt-2">
                  <div className="date-row-pill px-4">
                    {getDateTextLongFormat(new Date(chat?.createdAt), intl)}
                  </div>
                  <div className="date-row-line border-slate-200" />
                </div>
              )}
              <DrawerCommentSection
                height="26px"
                width="26px"
                containerWidth="auto"
                name={sender?.fullName}
                nameClass="font-18 font-bold hover:underline cursor-pointer"
                commentClass="font-16"
                src={sender?.avi}
                hours={time}
                comment={chat?.messageDescription}
                attachments={chat?.attachments}
                showName={true}
                msgId={chat?._id}
                senderId={chat?.sender?._id}
                files={chat?.files}
                context="task"
                roomId={taskRoom}
                taskId={taskId}
                senderName={sender?.fullName}
              />
            </div>
          );
        })}
      </div>
      {isModal && (
        <div className="task-comments-input-area pt-3">
          <TaskDetailsCommentsInput />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const createdBy = getOpenedTaskCreatedBy(state);

  return {
    allUsers: state.workspaceReducer.currentWorkspace.members,
    createdAt: getOpenedTaskCreatedAt(state),
    createdBy,
    deactivated: state.workspaceReducer.currentWorkspace.deactivated,
    taskComments: getOpenedTaskComments(state),
    taskCreatorAvi: getUserById(state, { userId: createdBy })?.avi,
    taskId: getOpenedTaskId(state),
    taskRoom: getOpenedTaskRoom(state),
    isTaskLoading: isOpenedTaskLoading(state),
    task: getOpenedTask(state),
  };
};

export default connect(mapStateToProps)(TaskDetailsCommentSection);
